import {
	IsBoolean,
	IsDateString,
	IsEnum,
	IsInt,
	IsNumber,
	IsOptional,
	IsString,
	Max,
	Min,
} from 'class-validator';
import type { SensorEntity, SensorReadingEntity } from '../entity';
import { SensorStatus, SensorType } from '../entity';

export class CreateSensorDto {
	@IsString()
	deviceId!: string;

	@IsOptional()
	@IsString()
	name?: string;

	@IsEnum(SensorType)
	type!: SensorType;

	@IsOptional()
	@IsInt()
	locationId?: number;

	@IsOptional()
	@IsEnum(SensorStatus)
	status?: SensorStatus;

	@IsOptional()
	@IsBoolean()
	powerOn?: boolean;

	@IsOptional()
	@IsString()
	firmwareVersion?: string;

	@IsOptional()
	@IsString()
	hardwareModel?: string;
}

export class UpdateSensorDto {
	@IsOptional()
	@IsString()
	name?: string;

	@IsOptional()
	@IsEnum(SensorType)
	type?: SensorType;

	@IsOptional()
	@IsInt()
	locationId?: number;

	@IsOptional()
	@IsEnum(SensorStatus)
	status?: SensorStatus;

	@IsOptional()
	@IsBoolean()
	powerOn?: boolean;

	@IsOptional()
	@IsDateString()
	lastSeen?: string;

	@IsOptional()
	@IsString()
	firmwareVersion?: string;

	@IsOptional()
	@IsString()
	hardwareModel?: string;

	@IsOptional()
	@IsString()
	lastErrorMessage?: string;

	@IsOptional()
	@IsInt()
	itemId?: number | null;

	@IsOptional()
	@IsInt()
	@Min(0)
	zoneDepthMm?: number | null;

	@IsOptional()
	@IsInt()
	@Min(0)
	minDistanceMm?: number | null;
}

export class IngestSensorReadingDto {
	@IsString()
	deviceId!: string;

	@IsOptional()
	@IsDateString()
	timestamp?: string;

	@IsOptional()
	@IsNumber()
	@Min(0)
	@Max(100)
	fillLevel?: number;

	@IsOptional()
	@IsNumber()
	@Min(-50)
	@Max(100)
	temperature?: number;

	@IsOptional()
	@IsNumber()
	@Min(0)
	@Max(100)
	humidity?: number;

	@IsOptional()
	@IsInt()
	@Min(-120)
	@Max(0)
	signalStrength?: number;

	@IsOptional()
	@IsNumber()
	@Min(0)
	@Max(100)
	batteryLevel?: number;
}

export class GetSensorReadingsQueryDto {
	@IsInt()
	sensorId!: number;

	@IsOptional()
	@IsDateString()
	startDate?: string;

	@IsOptional()
	@IsDateString()
	endDate?: string;

	@IsOptional()
	@IsInt()
	@Min(1)
	@Max(1000)
	limit?: number;

	@IsOptional()
	@IsInt()
	@Min(0)
	offset?: number;
}

export type SensorDto = SensorEntity;
export type SensorReadingDto = SensorReadingEntity;

export interface SensorPaginationDto {
	page: number;
	limit: number;
	total: number;
	totalPages: number;
}

export type GetSensorsResponseDto =
	| {
			success: true;
			data: Array<SensorDto>;
			pagination: SensorPaginationDto;
	  }
	| {
			success: false;
			error: string;
	  };

export type GetSensorResponseDto =
	| {
			success: true;
			data: SensorDto;
	  }
	| {
			success: false;
			error: string;
	  };

export type CreateSensorResponseDto =
	| {
			success: true;
			data: SensorDto;
	  }
	| {
			success: false;
			error: string;
	  };

export type UpdateSensorResponseDto =
	| {
			success: true;
			data: SensorDto;
	  }
	| {
			success: false;
			error: string;
	  };

export type DeleteSensorResponseDto =
	| {
			success: true;
	  }
	| {
			success: false;
			error: string;
	  };

export type GetSensorReadingsResponseDto =
	| {
			success: true;
			data: Array<SensorReadingDto>;
			pagination?: SensorPaginationDto;
	  }
	| {
			success: false;
			error: string;
	  };

export type IngestSensorReadingResponseDto =
	| {
			success: true;
			data: SensorReadingDto;
	  }
	| {
			success: false;
			error: string;
	  };
